import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { HOME_MEDIA, getApiBaseUrl, mediaUrl, publicMediaSrc } from "../config/api";
import AboutSeo from "../components/AboutSeo";
import useReveal from "../hooks/useReveal";
import aboutHero from "../assets/about-hero.jpg";
import aboutExpertise from "../assets/about-expertise.jpg";
import factoryBg from "../assets/factory-bg.jpg";
import whyUsBg from "../assets/whyus-bg.jpg";
import factoryIcon from "../assets/factory-icon.png";
import yourSiteIcon from "../assets/yoursite-icon.png";

const PAGE_GUTTER = "px-16 md:px-32 lg:px-40";

const industries = [
  { icon: "crane", label: "Contractors" },
  { icon: "building", label: "Builders & Developers" },
  { icon: "users", label: "Project Teams" },
  { icon: "factory", label: "Manufacturers" },
  { icon: "helmet", label: "Consultants" },
  { icon: "handshake", label: "Supplier Partners" },
];

const supportItems = [
  {
    icon: "search",
    title: "Product Sourcing",
    blurb: "Source steel, hardware and industrial products for your requirements.",
  },
  {
    icon: "clipboard",
    title: "Price & Availability",
    blurb: "Clear quotations based on current sourcing and availability.",
  },
  {
    icon: "box",
    title: "Flexible Quantities",
    blurb: "Support for loose pieces through to bulk requirements.",
  },
  {
    icon: "truck",
    title: "Delivery Coordination",
    blurb: "Practical coordination to get materials to your site.",
  },
];

const whyUs = [
  {
    icon: "bolt",
    title: "Lower Price, Shorter Lead Time",
    blurb: "A stockless model designed to reduce overheads and unnecessary handling.",
  },
  {
    icon: "box",
    title: "Loose or Bulk Quantities",
    blurb: "Source what your project needs, from individual pieces to bulk orders.",
  },
  {
    icon: "user",
    title: "One Reliable Trading Partner",
    blurb: "Steel, hardware and industrial supplies through a single responsive contact.",
  },
];

function Icon({ name, className = "h-7 w-7" }) {
  const common = {
    className,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.6",
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
  };
  if (name === "search") {
    return (
      <svg {...common}>
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l5 5" />
      </svg>
    );
  }
  if (name === "box") {
    return (
      <svg {...common}>
        <path d="M3 8l9-4 9 4-9 4-9-4z" />
        <path d="M3 8v8l9 4 9-4V8" />
        <path d="M12 12v8" />
      </svg>
    );
  }
  if (name === "tag") {
    return (
      <svg {...common}>
        <path d="M3 12V4h8l10 10-8 8L3 12z" />
        <circle cx="8" cy="8" r="1.2" />
      </svg>
    );
  }
  if (name === "layers") {
    return (
      <svg {...common}>
        <path d="M3 8l9-4 9 4-9 4-9-4z" />
        <path d="M3 12l9 4 9-4" />
        <path d="M3 16l9 4 9-4" />
      </svg>
    );
  }
  if (name === "truck") {
    return (
      <svg {...common}>
        <path d="M3 7h11v8H3z" />
        <path d="M14 10h4l3 3v2h-7" />
        <circle cx="7" cy="17" r="1.5" />
        <circle cx="18" cy="17" r="1.5" />
      </svg>
    );
  }
  if (name === "helmet") {
    return (
      <svg {...common}>
        <path d="M4 14a8 8 0 0116 0v2H4v-2z" />
        <path d="M3 16h18" />
        <path d="M12 6v2" />
      </svg>
    );
  }
  if (name === "clipboard") {
    return (
      <svg {...common}>
        <rect x="6" y="4" width="12" height="16" rx="1.5" />
        <path d="M9 4.5h6v2H9z" />
        <path d="M8 10h8M8 14h6" />
      </svg>
    );
  }
  if (name === "coin") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M9.5 10.5c.6-1 2-1.5 2.5-1.5s2 .6 2 1.8c0 2.2-5 1.2-5 3.4 0 1.2 1.2 2 2.5 2s1.9-.5 2.4-1.2" />
      </svg>
    );
  }
  if (name === "target") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="12" r="1.2" />
      </svg>
    );
  }
  if (name === "crane") {
    return (
      <svg {...common}>
        <path d="M4 20h16" />
        <path d="M7 20V8h3l8-4v3" />
        <path d="M10 8v12" />
        <path d="M18 7v9" />
        <path d="M16 16h4v4h-4z" />
      </svg>
    );
  }
  if (name === "building") {
    return (
      <svg {...common}>
        <path d="M4 20V6l8-3 8 3v14" />
        <path d="M9 20v-6h6v6" />
        <path d="M9 10h.01M15 10h.01M12 10h.01" />
      </svg>
    );
  }
  if (name === "users") {
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19a5.5 5.5 0 0111 0" />
        <circle cx="17" cy="9" r="2.4" />
        <path d="M16 19a4.8 4.8 0 014.5-4.2" />
      </svg>
    );
  }
  if (name === "factory") {
    return (
      <svg {...common}>
        <path d="M3 20V10l6 4V10l6 4V8l6 3v9H3z" />
        <path d="M7 20v-3M12 20v-3M17 20v-3" />
      </svg>
    );
  }
  if (name === "bolt") {
    return (
      <svg {...common}>
        <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
      </svg>
    );
  }
  if (name === "user") {
    return (
      <svg {...common}>
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5 20a7 7 0 0114 0" />
      </svg>
    );
  }
  if (name === "handshake") {
    return (
      <svg {...common}>
        <path d="M8 11l3-3 2 2 3-3" />
        <path d="M4 13l4-2 3 3 2-1 3 2 4-3" />
        <path d="M7 16l2.5 2.5a2 2 0 002.8 0L14 17" />
      </svg>
    );
  }
  return null;
}

function AboutPage() {
  const pageRef = useRef(null);
  const [contact, setContact] = useState(null);
  const [contactStatus, setContactStatus] = useState("loading");

  useReveal(pageRef);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nodes = pageRef.current?.querySelectorAll(".hero-in");
      nodes?.forEach((node) => {
        if (getComputedStyle(node).opacity === "0") node.classList.add("is-visible");
      });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) {
          setContact(Array.isArray(list) && list[0] ? list[0] : null);
          setContactStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContact(null);
          setContactStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const siteName = String(contact?.siteName || "").trim();
  const companyName = String(contact?.companyName || "").trim();
  const siteLogo = publicMediaSrc(contact?.siteLogo) || mediaUrl(HOME_MEDIA.logo);

  return (
    <div className="about-page" ref={pageRef}>
      <AboutSeo contact={contact} contactStatus={contactStatus} />
      <section className="relative overflow-hidden bg-at-dark text-white">
        <img
          src={aboutHero}
          alt=""
          width={1024}
          height={342}
          fetchPriority="high"
          className="hero-zoom-subtle pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="pointer-events-none absolute inset-0 bg-black/20" />
        <div className="pointer-events-none absolute inset-y-0 left-0 hidden h-full w-[58%] bg-gradient-to-r from-black/75 via-black/40 to-transparent md:block" />
        <div className={`relative z-10 flex min-h-[320px] flex-col justify-between py-10 md:min-h-[420px] md:py-12 ${PAGE_GUTTER}`}>
          <nav aria-label="Breadcrumb" className="hero-in text-xs text-white/70">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="px-2 text-white/40">/</span>
            <span className="text-white">About Us</span>
          </nav>
          <div className="max-w-xl">
            <p className="hero-in hero-in-1 mt-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              <span className="inline-block h-1 w-12 bg-at-gold" aria-hidden="true" />
              Who We Are
            </p>
            <h1 className="hero-in hero-in-2 mt-2 font-serif text-3xl uppercase leading-tight md:text-5xl">
              Your Industrial
              <br />
              <span className="text-at-gold">Supply Partner</span>
            </h1>
            <p className="hero-in hero-in-3 mt-3 max-w-md text-sm leading-relaxed text-white/70">
              Practical sourcing support for contractors, builders and businesses across Malaysia.
            </p>
          </div>
        </div>
      </section>

      <section className="about-section">
        <svg width="0" height="0" aria-hidden="true" focusable="false">
          <defs>
            <clipPath id="aboutPageCurve" clipPathUnits="objectBoundingBox">
              <path d="M 0 0 H 0.46 C 0.47 0.25, 0.49 0.55, 0.52 1 H 0 Z" />
            </clipPath>
          </defs>
        </svg>
        <img
          src={mediaUrl(HOME_MEDIA.about)}
          alt="Industrial worker handling steel hollow sections"
          loading="lazy"
          data-reveal=""
          data-reveal-delay="1"
          className="about-image object-right"
        />
        <div className="about-content-background" />
        <div className="about-content">
          <div className="about-inner">
            <div className="about-copy" data-reveal="">
              <span className="mb-4 block h-1 w-12 bg-at-gold" aria-hidden="true" />
              <h2 className="font-serif text-3xl uppercase leading-snug text-at-dark md:text-4xl">
                Materials That Keep
                <br />
                Your Business Moving.
              </h2>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-neutral-600">
                {companyName ? `${companyName} provides` : "We provide"} dependable steel, hardware and industrial products for contractors, builders and businesses across Malaysia.
              </p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-600">
                Our aim is to be a one-stop trading partner, supporting your project requirements from small jobs to large-scale developments with practical sourcing solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#eeeeee] py-14 md:py-20">
        <div className={`grid items-start gap-10 md:grid-cols-2 md:gap-16 ${PAGE_GUTTER}`}>
          <img
            src={aboutExpertise}
            alt="Construction drawings and steel samples used for material planning"
            width={1024}
            height={602}
            loading="lazy"
            data-reveal=""
            className="w-full object-cover"
          />
          <div className="expertise-copy" data-reveal="" data-reveal-delay="1">
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
              <span className="inline-block h-1 w-12 shrink-0 bg-at-gold" aria-hidden="true" />
              Built on Industry Knowledge
            </p>
            <h2 className="mt-3 font-serif text-3xl uppercase leading-snug text-at-dark md:text-4xl">
              Expertise You Can Trust
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-neutral-600">
              Founded by a Quantity Surveyor with extensive construction industry experience, we understand budgeting, material selection and cost efficiency throughout a project.
            </p>
            <ul className="mt-8 grid grid-cols-3 gap-4">
              <li className="expertise-item text-center">
                <Icon name="helmet" className="mx-auto h-7 w-7 text-at-gold" />
                <h3 className="mt-3 text-xs font-semibold leading-snug text-at-dark sm:text-sm">
                  Practical Material
                  <br />
                  Advice
                </h3>
              </li>
              <li className="expertise-item text-center">
                <Icon name="coin" className="mx-auto h-7 w-7 text-at-gold" />
                <h3 className="mt-3 text-xs font-semibold leading-snug text-at-dark sm:text-sm">Cost-Conscious Sourcing</h3>
              </li>
              <li className="expertise-item text-center">
                <Icon name="clipboard" className="mx-auto h-7 w-7 text-at-gold" />
                <h3 className="mt-3 text-xs font-semibold leading-snug text-at-dark sm:text-sm">Project-Focused Support</h3>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={PAGE_GUTTER}>
          <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold" data-reveal="">
            <span className="inline-block h-1 w-12 shrink-0 bg-at-gold" aria-hidden="true" />
            How We Help
          </p>
          <h2 className="mt-3 font-serif text-3xl uppercase text-at-dark md:text-4xl" data-reveal="" data-reveal-delay="1">
            How We Support Your Business
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {supportItems.map((item, index) => (
              <article
                key={item.title}
                data-reveal=""
                style={{ "--reveal-i": index + 1 }}
                className="support-card border border-at-gold px-5 py-8 text-center"
              >
                <Icon name={item.icon} className="mx-auto h-8 w-8 text-at-dark" />
                <h3 className="mt-4 text-sm font-semibold text-at-dark">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-neutral-500">{item.blurb}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="factory-price-section relative overflow-hidden text-white" data-reveal="">
        <img src={factoryBg} alt="" width={1024} height={341} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className={`relative z-10 py-16 text-center ${PAGE_GUTTER}`}>
          <p className="factory-copy flex items-center justify-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold">
            <span className="inline-block h-1 w-12 shrink-0 bg-at-gold" aria-hidden="true" />
            A one-stop trading partner for contractors
          </p>
          <h2 className="factory-copy factory-copy-2 mt-3 font-serif text-3xl uppercase leading-snug md:text-4xl">
            Factory Price. Trader Speed.
          </h2>
          <p className="factory-copy factory-copy-3 mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/75">
            We&apos;re a stockless trading house — no warehouse cost baked into your price, no double-handling markup. We source direct per order and get it to your site fast.
          </p>
          <div className="mt-10 flex items-end justify-center gap-4 sm:gap-6">
            <div className="factory-step factory-step-1 flex flex-col items-center text-center">
              <img src={factoryIcon} alt="" className="h-16 w-16 object-contain sm:h-20 sm:w-20" />
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">Supplier</span>
            </div>
            <span className="factory-arrow factory-arrow-1 mb-8 text-2xl text-at-gold">→</span>
            <div className="factory-step factory-step-2 flex flex-col items-center text-center">
              <img src={siteLogo} alt="" className="h-14 w-14 object-contain invert sm:h-16 sm:w-16" />
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">
                {siteName || "AT Industrial"}
              </span>
            </div>
            <span className="factory-arrow factory-arrow-2 mb-8 text-2xl text-at-gold">→</span>
            <div className="factory-step factory-step-3 flex flex-col items-center text-center">
              <img src={yourSiteIcon} alt="" className="h-16 w-16 object-contain sm:h-20 sm:w-20" />
              <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">Your Site</span>
            </div>
          </div>
          <p className="factory-location mt-6 flex items-center justify-center gap-2 text-xs text-white/70">
            <svg className="h-4 w-4 shrink-0 text-at-gold" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z" />
              <circle cx="12" cy="10" r="2.2" />
            </svg>
            Based in Klang Valley, supplying across Malaysia.
          </p>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className={PAGE_GUTTER}>
          <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold" data-reveal="">
            <span className="inline-block h-1 w-12 shrink-0 bg-at-gold" aria-hidden="true" />
            Who We Work With
          </p>
          <h2 className="mt-3 font-serif text-3xl uppercase text-at-dark md:text-4xl" data-reveal="" data-reveal-delay="1">
            Supporting Businesses Across Industries
          </h2>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            {industries.map((item, index) => (
              <li
                key={item.label}
                data-reveal=""
                style={{ "--reveal-i": index + 1 }}
                className="industry-tile border border-at-gold px-4 py-8 text-center"
              >
                <Icon name={item.icon} className="mx-auto h-8 w-8 text-at-dark" />
                <h3 className="mt-4 text-sm font-semibold text-at-dark">{item.label}</h3>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="why-us-section relative overflow-hidden text-white">
        <img src={whyUsBg} alt="" width={1024} height={341} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className={`relative z-10 py-16 ${PAGE_GUTTER}`}>
          <p className="flex items-center justify-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-at-gold" data-reveal="">
            <span className="inline-block h-1 w-12 shrink-0 bg-at-gold" aria-hidden="true" />
            The {siteName || "Industrial"} Difference
          </p>
          <h2 className="mt-3 text-center font-serif text-3xl uppercase md:text-4xl" data-reveal="" data-reveal-delay="1">
            Why Work With Us?
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {whyUs.map((item, index) => (
              <article
                key={item.title}
                data-reveal=""
                data-reveal-delay={index === 0 ? undefined : String(index)}
                className="why-card border-t-2 border-at-gold bg-[#2a2a2a] px-6 py-8 text-center"
              >
                <Icon name={item.icon} className="mx-auto h-8 w-8 text-at-gold" />
                <h3 className="mt-5 text-sm font-semibold uppercase tracking-wide">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">{item.blurb}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
